import { useMemo } from "react"
import { useGetJobsJobIdCandidate } from "openapi/api/endpoints"

type UseResolvedPlacementJobCandidateArgs = {
  enabled: boolean
  jobId: string
  candidateId: string
  jobCandidateId?: string
  candidateName?: string
}

/**
 * Resolves the pipeline row a placement must write to.
 *
 * Placement creation can start from a job pipeline row, where job_candidate_id is
 * already known, or from a candidate/job context, where the form only has both
 * parent ids. This hook keeps that fallback lookup out of the dialog and always
 * returns the id that should be submitted to the placement endpoint.
 */
export function useResolvedPlacementJobCandidate({
  enabled,
  jobId,
  candidateId,
  jobCandidateId,
  candidateName,
}: UseResolvedPlacementJobCandidateArgs) {
  const shouldResolveJobCandidate = enabled && !jobCandidateId && !!jobId && !!candidateId
  const {
    data: jobCandidate,
    error: jobCandidateError,
    isLoading: isLoadingJobCandidate,
  } = useGetJobsJobIdCandidate(
    jobId,
    { candidate_id: candidateId },
    { query: { enabled: shouldResolveJobCandidate } },
  )

  const resolvedJobCandidateId = jobCandidateId || jobCandidate?.job_candidate_id || ""
  const isResolvingJobCandidate = shouldResolveJobCandidate && isLoadingJobCandidate
  const hasJobCandidateResolutionError =
    enabled && !isResolvingJobCandidate && !resolvedJobCandidateId

  const resolvedCandidateName = useMemo(() => {
    const explicitName = candidateName?.trim()
    if (explicitName) return explicitName

    return (
      jobCandidate?.full_name ??
      jobCandidate?.candidates?.raw_json?.full_name ??
      [jobCandidate?.candidates?.raw_json?.first_name, jobCandidate?.candidates?.raw_json?.last_name]
        .filter(Boolean)
        .join(" ")
    )
  }, [candidateName, jobCandidate])

  return {
    jobCandidate,
    jobCandidateError,
    resolvedJobCandidateId,
    resolvedCandidateName,
    isResolvingJobCandidate,
    hasJobCandidateResolutionError,
  }
}
